import { useState } from 'react';
import { Dialog, DialogContent, Box, Typography, IconButton, Button } from '@mui/material';
import { Close as CloseIcon, Print as PrintIcon } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';

/**
 * JobLocationQRPoster - Generates printable 8.5" x 11" job location poster
 * Layout: Top 1/3 = Job info, Bottom 2/3 = QR code
 * Used to hang above job stacks in warehouse
 */
export default function JobLocationQRPoster({ job, open, onClose }) {
  const [isPrinting, setIsPrinting] = useState(false);

  if (!job) return null;

  // Use full URL with domain so QR code works on any device
  const jobUrl = `${window.location.origin}/jobs/${job.id}`;
  const jobNumber = job.job_number || job.id;
  const jobName = job.name || 'Unnamed Job';

  const handlePrint = () => {
    setIsPrinting(true);
    // Small delay to ensure dialog is hidden before printing
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        setIsPrinting(false);
        onClose();
      }, 500);
    }, 100);
  };

  return (
    <>
      {/* Print-only styles */}
      <style>
        {`
          @media print {
            @page {
              size: letter portrait;
              margin: 0.5in;
            }

            /* Hide everything except the printing dialog */
            body > * {
              display: none !important;
            }

            body > .MuiModal-root.printing {
              display: block !important;
            }

            /* Remove dialog styling for print */
            .MuiDialog-root.printing {
              position: static !important;
            }

            .MuiDialog-root.printing .MuiBackdrop-root {
              display: none !important;
            }

            .MuiDialog-root.printing .MuiDialog-container {
              position: static !important;
              display: block !important;
            }

            .MuiDialog-root.printing .MuiDialog-paper {
              position: static !important;
              margin: 0 !important;
              padding: 0 !important;
              max-width: none !important;
              box-shadow: none !important;
              background: white !important;
            }

            .MuiDialog-root.printing .MuiDialogContent-root {
              padding: 0 !important;
              margin: 0 !important;
            }

            /* Hide header/buttons in print */
            .MuiDialog-root.printing .MuiDialogContent-root > div:first-child {
              display: none !important;
            }

            /* Make poster fill the page */
            #job-qr-poster {
              width: 8.5in !important;
              height: 11in !important;
              margin: 0 !important;
              border: none !important;
              max-width: none !important;
            }
          }
        `}
      </style>

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        className={isPrinting ? "printing" : "no-print"}
      >
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Job Location QR Poster Preview</Typography>
            <Box>
              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                sx={{ mr: 1 }}
              >
                Print
              </Button>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box
            id="job-qr-poster"
            sx={{
              width: '8.5in',
              height: '11in',
              maxWidth: '100%',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #ccc',
              backgroundColor: 'white',
              overflow: 'hidden'
            }}
          >
            {/* Top 1/3: Job Information */}
            <Box
              sx={{
                height: '33.33%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '1in 0.5in',
                borderBottom: '2px solid #333'
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '5rem',
                  lineHeight: 1.2,
                  textAlign: 'center',
                  marginBottom: '1rem'
                }}
              >
                {jobNumber}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontSize: '2.5rem',
                  lineHeight: 1.3,
                  textAlign: 'center',
                  wordWrap: 'break-word',
                  maxWidth: '100%',
                  padding: '0 0.5in'
                }}
              >
                {jobName}
              </Typography>
            </Box>

            {/* Bottom 2/3: QR Code */}
            <Box
              sx={{
                height: '66.67%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '0.5in'
              }}
            >
              <QRCodeSVG
                value={jobUrl}
                size={700}
                level="M"
                includeMargin={true}
                style={{
                  width: '100%',
                  height: '100%',
                  maxWidth: '7.5in',
                  maxHeight: '7.5in'
                }}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
